// ── Editor read scope: cancel in-flight GETs when the user changes tab ──────
// Clicking through the workflow editor's tabs used to leave every request the
// abandoned tab had started running to completion: the browser kept the
// connection open, studio-backend kept executing the query (full-config alone
// is ~20 sequential DB round trips), and the tab the user actually wanted
// waited behind them. None of the editor panels owned an AbortController, and
// the `let cancelled = false` guards several of them do have only DISCARD the
// response — they never free the socket.
//
// This is the same shape the dashboard sidebar already uses
// (NavigationTreeContext's shared controller, aborted on every sub-tab
// activation), lifted to the axios instance so it covers the editor's ~50 read
// call sites without threading a `signal` argument through 35 api functions.
//
// Three rules keep it safe:
//   1. GET only. A POST/PUT/DELETE is never scoped — aborting a save is how
//      you lose a user's work.
//   2. A request that already carries its own `signal` is left alone, so every
//      caller with a hand-rolled AbortController (NavigationTreeContext, the
//      monitoring panels, viewsApi/screensApi's `{ signal }` callers) keeps
//      owning its own cancellation.
//   3. `__noScope: true` opts a request out entirely. Required for any read
//      whose promise is SHARED between callers — readCache's `dedupedRead`
//      hands one promise to everybody, so aborting it for the outgoing tab
//      would break the incoming tab that just subscribed to it.
import axios from "axios";

let controller = new AbortController();

/** The signal every scoped read is currently issued under. */
export const currentReadSignal = () => controller.signal;

// ── Opt-in link for callers that own their own controller ───────────────────
// Rule 2 above leaves a request that carries its own `signal` alone, and that
// is right for a reader which OUTLIVES the navigation: the editor's
// NavigationTreeContext sits in the sidebar across every tab change, so
// scope-aborting its read would empty the sidebar.
//
// It is wrong for a panel whose controller exists only to supersede its own
// previous request — every monitoring log panel. Those are the heaviest reads
// on the page, and skipping them would leave the abort covering only the light
// ones. Such a panel calls `linkToReadScope(controller)` and gets navigation
// cancellation on top of the supersede it already had.
//
// Opt-in rather than "combine every caller's signal with the scope" because
// the two cases are indistinguishable from inside the interceptor — only the
// caller knows whether it outlives the navigation.
const linked = new Set();

/**
 * Tie a caller-owned AbortController to the current read scope, so the next
 * `abortInFlightReads` cancels it too. Returns an unlink function.
 *
 * The controller drops itself from the set as soon as it aborts for any reason
 * (superseded by a newer request, or unmount cleanup), so the set only ever
 * holds live requests and never keeps a component alive.
 */
export const linkToReadScope = (ctrl) => {
  if (!ctrl || ctrl.signal.aborted) return () => {};
  linked.add(ctrl);
  const unlink = () => linked.delete(ctrl);
  ctrl.signal.addEventListener("abort", unlink, { once: true });
  return unlink;
};

/**
 * Cancel every scoped read that is still in flight and open a fresh scope.
 *
 * Call this from the navigation EVENT (a tab button's onClick), not from an
 * effect. An effect-based abort races React's effect ordering — child effects
 * run before the parent's, and `useLayoutEffect` runs before any passive
 * cleanup — so it would cancel the INCOMING panel's own requests.
 */
export const abortInFlightReads = (reason = "navigation") => {
  const previous = controller;
  // Snapshot before the aborts below: each one fires the listener that removes
  // it from `linked`, which would mutate the set mid-iteration.
  const opted = Array.from(linked);
  linked.clear();
  // Swap first: an abort listener that synchronously starts a new request must
  // get the fresh signal, not the one being torn down.
  controller = new AbortController();
  previous.abort(reason);
  opted.forEach((ctrl) => {
    try {
      ctrl.abort(reason);
    } catch {
      /* already aborted, or a controller that refuses a reason */
    }
  });
};

/**
 * True for a request we cancelled ourselves. An aborted read is treated as
 * "never happened" — no error state, no toast, no console noise; re-entering
 * the tab starts a clean fetch.
 */
export const isCanceled = (err) =>
  axios.isCancel(err) ||
  err?.code === "ERR_CANCELED" ||
  err?.name === "AbortError" ||
  err?.name === "CanceledError";

/**
 * Axios config for reads whose promise is SHARED between callers — the
 * module-level caches in api.js and every readCache `dedupedRead`. One caller
 * navigating away must not abort a request another caller is still waiting on.
 */
export const NO_SCOPE = { __noScope: true };

/** Attach the scope to an axios instance. Call once, at creation time. */
export const attachReadScope = (axiosInstance) => {
  axiosInstance.interceptors.request.use((config) => {
    if (config.__noScope) return config;
    if (config.signal) return config; // caller owns its own cancellation
    if ((config.method || "get").toLowerCase() !== "get") return config;
    config.signal = currentReadSignal();
    return config;
  });
};
